import * as React from 'react';
import {Box, BoxProps} from '@material-ui/core';
import styled, {css} from 'styled-components';
import Skeleton, {SkeletonProps} from '@material-ui/lab/Skeleton';
import {conditionalStyles} from '../../../utils/ThemeUtils';

//https://styled-components.com/docs/api#transient-props
interface TransientProps {
    $turnOverAxis?: boolean;
}

const mixin = css`
    border-radius: ${p => p.theme.muiTheme.shape.borderRadius}px;
`;

const SkeletonContainer = styled(Skeleton)<TransientProps>`
    display: flex;
    flex-grow: 1;
    height: 100%;
    min-height: 100%;
    margin-bottom: ${p => p.theme.muiTheme.spacing(2)}px;

    ${p =>
        conditionalStyles(p.$turnOverAxis)(
            `margin-right: ${p.theme.muiTheme.spacing(2)}px`,
            `margin-left: ${p.theme.muiTheme.spacing(2)}px`
        )};

    ${mixin}
`;

const SkeletonXAxis = styled(Skeleton)<TransientProps>`
    width: 100%;
    max-height: ${p => p.theme.muiTheme.spacing(1)}px;
    min-height: ${p => p.theme.muiTheme.spacing(1)}px;
    flex-grow: 1;

    ${p =>
        conditionalStyles(p.$turnOverAxis)(
            `margin-right: ${p.theme.muiTheme.spacing(3)}px`,
            `margin-left: ${p.theme.muiTheme.spacing(3)}px`
        )};

    ${mixin}
`;

const SkeletonYAxis = styled(Skeleton)<TransientProps>`
    height: 100%;
    min-height: 100%;
    width: ${p => p.theme.muiTheme.spacing(1)}px;

    ${mixin}
`;

const SkeletonLegendItem = styled(Skeleton)`
    width: 87px;
    height: ${p => p.theme.muiTheme.spacing(2)}px;
    max-height: ${p => p.theme.muiTheme.spacing(2)}px;

    margin: ${p => p.theme.muiTheme.spacing(0, 4, 0, 0)};

    ${mixin};
`;

const LEGEND_ITEMS_COUNT = 5;

const createList = (length: number): string[] =>
    new Array(length).fill(null).map((_element, index) => index.toString(36));

export interface ChartSkeletonProps extends BoxProps {
    animation?: SkeletonProps['animation'];
    hasLegend?: boolean;
    turnOver?: boolean;
    turnOverAxis?: boolean;
}

const ChartSkeleton: React.FC<ChartSkeletonProps> = ({
    animation,
    height,
    width = '100%',
    hasLegend = true,
    turnOver,
    turnOverAxis,
    ...props
}: ChartSkeletonProps) => {
    return (
        <Box
            display="flex"
            flexDirection={turnOver ? 'column-reverse' : 'column'}
            width={width}
            height={height}
            {...props}>
            <Box
                display="flex"
                height="100%"
                width="100%"
                flexWrap="wrap"
                flexDirection={turnOverAxis ? 'row-reverse' : 'row'}>
                <SkeletonYAxis variant="rect" animation={animation} />
                <SkeletonContainer
                    variant="rect"
                    animation={animation}
                    $turnOverAxis={turnOverAxis}
                />
                <SkeletonXAxis variant="rect" animation={animation} $turnOverAxis={turnOverAxis} />
            </Box>
            {hasLegend && (
                <Box display="flex" ml={3} mt={turnOver ? 0 : 6} mb={turnOver ? 6 : 0}>
                    {createList(LEGEND_ITEMS_COUNT).map(key => (
                        <SkeletonLegendItem key={key} variant="rect" animation={animation} />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default ChartSkeleton;
